<!--
  A peek at a definition: its head line, the two lines before it (the end
  of its leading comments and attributes) and a few after, highlighted as
  in the diff, in a
  card under the thing hovered (the scope crumb's name, or the chip on the
  definition's last line). It takes no pointer or focus: moving away from
  what opened it closes it, and a click there goes to the definition.
-->
<script lang="ts">
  import type { Scope, Span } from '../engine/types';
  import { renderLine } from '../logic/render';

  interface Props {
    scope: Scope;
    /** The side's lines and highlight (null until it is highlighted). */
    lines: readonly string[];
    hl: readonly (readonly Span[])[] | null;
    /** What opened it, to place it under (or over, without room below). */
    anchor: DOMRect;
    /** What its header names the side as ("HEAD", "BASE"). */
    sideLabel: string;
  }
  let { scope, lines, hl, anchor, sideLabel }: Props = $props();

  /** How many lines before the head line (leading comments, attributes) and after it are shown at most. */
  const BEFORE_HEAD = 2;
  const AFTER_HEAD = 8;

  const first = $derived(Math.max(scope.startLine, scope.headLine - BEFORE_HEAD));
  const end = $derived(Math.min(scope.endLine, scope.headLine + AFTER_HEAD));
  const shown = $derived(Array.from({ length: Math.max(0, end - first + 1) }, (_, i) => first + i));
  const above = $derived(first - scope.startLine);
  const more = $derived(scope.endLine - end);

  let card: HTMLElement | null = $state(null);
  const pos = $derived.by(() => {
    const h = card?.offsetHeight ?? 0;
    const w = card?.offsetWidth ?? 0;
    const left = Math.max(16, Math.min(anchor.left, innerWidth - w - 16));
    const below = anchor.bottom + 6;
    const top = below + h > innerHeight - 8 && anchor.top - 6 - h > 8 ? anchor.top - 6 - h : below;
    return { left, top };
  });
</script>

<div class="scope-peek" bind:this={card} role="tooltip" style:left="{pos.left}px" style:top="{pos.top}px">
  <div class="scope-peek-head">
    <b>{scope.title}</b>
    <span>{sideLabel} {scope.headLine}{scope.endLine > scope.headLine ? `–${scope.endLine}` : ''}</span>
    <span class="scope-peek-go">click to go</span>
  </div>
  <div class="scope-peek-code">
    {#if above > 0}<div class="scope-peek-more">… {above} line{above === 1 ? '' : 's'} of comments and attributes above</div>{/if}
    {#each shown as n (n)}
      <div class="scope-peek-line" class:head={n === scope.headLine}>
        <span class="gutter">{n}</span><code>{@html renderLine(lines[n - 1] ?? '', hl?.[n - 1] ?? [])}</code>
      </div>
    {/each}
    {#if more > 0}<div class="scope-peek-more">… {more} more line{more === 1 ? '' : 's'}</div>{/if}
  </div>
</div>

<style>
  .scope-peek {
    position: fixed;
    z-index: 70;
    pointer-events: none;
    width: max-content;
    max-width: min(720px, calc(100vw - 32px));
    background: var(--surface);
    color: var(--fg);
    border: 1px solid var(--line);
    border-radius: 8px;
    box-shadow: 0 12px 32px #0000002e;
    overflow: hidden;
    font: 11px ui-monospace, SFMono-Regular, Consolas, monospace;
  }
  .scope-peek-head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 6px 10px;
    border-bottom: 1px solid var(--line);
    background: var(--code);
    color: var(--muted);
  }
  .scope-peek-head b {
    color: var(--fg);
    font-weight: 600;
  }
  .scope-peek-go {
    margin-left: auto;
    font: 10px system-ui, sans-serif;
  }
  .scope-peek-code {
    padding: 4px 0;
  }
  .scope-peek-line {
    display: flex;
    font-size: 12px;
    line-height: 1.6;
    white-space: pre;
    overflow: hidden;
  }
  .scope-peek-line.head {
    background: var(--soft);
  }
  .scope-peek-line .gutter {
    flex: none;
    width: 44px;
    padding-right: 10px;
    text-align: right;
    color: var(--gutter, var(--muted));
    user-select: none;
  }
  .scope-peek-line code {
    background: none;
    padding: 0;
    border: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 12px;
  }
  .scope-peek-more {
    padding: 2px 10px 2px 54px;
    color: var(--muted);
    font-size: 10px;
  }
</style>
